// ─── Email validator + synthetic email helpers ───────────────────────────────
//
// Synthetic emails (`*.field.rascal.local`) cover field staff who don't have
// any real email — phone becomes their only reachable channel. See plan §D10
// and memory `contacts_directory_decision.md`.
//
// All contacts MUST have an `email` (it's the primary unique key). When real
// email is absent, HR generates `{employee_id}@field.rascal.local`. UI
// suppresses email delivery for synthetic emails and forces WA channel.

/** Synthetic email domain. NEVER resolves to a real MX server — only used
 *  internally as a unique identifier for contacts without real email. */
export const SYNTHETIC_EMAIL_DOMAIN = 'field.rascal.local';

// Simplified RFC 5322 — sufficient for sanity check (full RFC is impractically
// permissive). Catches common typos like missing TLD, spaces, double `@`.
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Lowercase + trim. Always run before storing or comparing emails. */
export function normalizeEmail(input: string): string {
  return input.trim().toLowerCase();
}

/** True if input passes basic email format validation. */
export function isValidEmail(input: string | null | undefined): boolean {
  if (!input || typeof input !== 'string') return false;
  return EMAIL_REGEX.test(normalizeEmail(input));
}

/** True if the email ends with the synthetic domain `field.rascal.local`. */
export function isSyntheticEmail(email: string): boolean {
  return normalizeEmail(email).endsWith(`@${SYNTHETIC_EMAIL_DOMAIN}`);
}

/** Generate a synthetic email for a contact without real email.
 *  Strips non-alphanumeric (except `-`) from the employee_id for safety. */
export function generateSyntheticEmail(employeeId: string): string {
  const safe = employeeId.toLowerCase().replace(/[^a-z0-9-]/g, '');
  if (!safe) {
    throw new Error('employeeId yields empty synthetic local part');
  }
  return `${safe}@${SYNTHETIC_EMAIL_DOMAIN}`;
}
